import React, { PropsWithChildren, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { IconButton } from '@producer/ui-kit';

import { Delete as DeleteIcon } from '../../../components/icons';
import { getNameInitials } from '../../../lib/utils/get-name-initials';
import { getFullName } from '../../../lib/utils/get-full-name';

import type { Project } from '../../../app/entities/project';
import type { TeamContact } from '../../teams/models/team';

import styles from './CollaboratorCard.module.scss';
type CollaboratorCardProps = {
  project: Project;
  contact: TeamContact;
};

export const CollaboratorCard: React.FC<CollaboratorCardProps> = observer(
  ({ project, contact }) => {
    const handleDelete = () => {
      project.removeKeyContact(contact._id);
    };

    const contactName = getFullName(contact);

    return (
      <article className={styles['card']}>
        <div className={styles['content']}>
          <div className={styles['delete']}>
            <IconButton icon={<DeleteIcon />} onClick={handleDelete} />
          </div>

          <Avatar alt={contactName} src={contact.avatar || ''} />

          <p className={styles['name']}>{contactName}</p>
          <p className={styles['role']}>{contact.role}</p>
        </div>
      </article>
    );
  },
);

type AvatarProps = {
  alt?: string;
  src?: string;
};

/**
 * If a src is provided, it will always show the picture
 * if the image loading ends up an error, it will show:
 *  - the children if provided
 *  - the first letter of the alt text if provided
 *  - a default user icon
 * If no src is provided, it will show:
 *  - the children if provided
 *  - a default user icon
 *
 * @constructor
 */
const Avatar: React.FC<PropsWithChildren<AvatarProps>> = ({ alt, src, children }) => {
  const [hasError, setHasError] = useState(false);

  const handleImageError = () => {
    setHasError(true);
  };

  return (
    <div className={styles['avatar-container']}>
      {hasError ? (
        <div>{getNameInitials(alt)}</div>
      ) : src ? (
        <img className={styles['image']} src={src} onError={handleImageError} />
      ) : children ? (
        children
      ) : (
        getNameInitials(alt)
      )}
    </div>
  );
};
